import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Radio, TrendingUp, TrendingDown, Minus, RefreshCw, AlertTriangle } from 'lucide-react';
import { quantLiveQuote, getErrorMessage } from '../../lib/api';

/**
 * Card "Live" — quote real-time via Alpaca per US equity/ETF.
 *
 * Mostra: prezzo corrente, change vs prev close, bid/ask, daily H/L, volume,
 * timestamp. Auto-refresh ogni 30s. Disabilita refresh per ticker non-US
 * (mostra info message).
 *
 * Stato 503: provider Alpaca non configurato → CTA verso /admin/market-providers.
 *
 * Props:
 *  - symbol: ticker da seguire (es. 'SPY')
 *  - assetMeta: optional {class, name} per decidere se è US
 */
const US_CLASSES = new Set([
  'etf_core', 'etf_sector', 'etf_commodity', 'etf_bond', 'etf_volatility',
  'etf_other', 'stock',
]);

const fmtPrice = (v) => (v == null ? '—' : Number(v).toFixed(2));
const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`);
const fmtVol = (v) => {
  if (v == null) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

const LiveQuoteCard = ({ symbol, assetMeta }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providerMissing, setProviderMissing] = useState(false);
  const timerRef = useRef(null);

  // Asset compatibility check: Alpaca free tier copre solo US equities/ETF
  const isUSAsset = !assetMeta?.class || US_CLASSES.has(assetMeta.class);

  const fetchQuote = useCallback(async () => {
    if (!symbol || !isUSAsset) return;
    setLoading(true);
    setError(null);
    try {
      const r = await quantLiveQuote(symbol);
      setQuote(r.data);
      setProviderMissing(false);
    } catch (e) {
      if (e.response?.status === 503) {
        setProviderMissing(true);
      } else {
        setError(getErrorMessage(e, 'Errore quote live'));
      }
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [symbol, isUSAsset]);

  useEffect(() => {
    fetchQuote();
    // Auto-refresh 30s. Niente WS per ora — polling è enough per IEX free tier.
    timerRef.current = setInterval(fetchQuote, 30000);
    return () => clearInterval(timerRef.current);
  }, [fetchQuote]);

  if (!isUSAsset) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-3 text-xs text-muted-foreground flex items-center gap-2">
          <Radio className="w-3.5 h-3.5" />
          Live mode non disponibile per <code className="font-mono">{symbol}</code> —
          Alpaca free tier copre solo US equities/ETF. Asset class: <code className="font-mono">{assetMeta?.class || '?'}</code>.
        </CardContent>
      </Card>
    );
  }

  if (providerMissing) {
    return (
      <Card className="border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20">
        <CardContent className="py-3 text-xs flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Alpaca non configurato</p>
            <p className="text-muted-foreground mt-0.5">
              Per il live mode serve aggiungere il provider Alpaca.{' '}
              <a href="/admin/market-providers" className="text-primary hover:underline">
                Configura qui
              </a>
              {' '}(formato chiave: <code className="font-mono">KEY_ID:SECRET_KEY</code>).
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const changePct = quote?.change_pct;
  const trendIcon = changePct == null
    ? <Minus className="w-4 h-4" />
    : changePct > 0
      ? <TrendingUp className="w-4 h-4 text-emerald-500" />
      : changePct < 0
        ? <TrendingDown className="w-4 h-4 text-red-500" />
        : <Minus className="w-4 h-4" />;
  const trendColor = changePct == null ? 'text-muted-foreground'
    : changePct > 0 ? 'text-emerald-600 dark:text-emerald-400'
    : changePct < 0 ? 'text-red-600 dark:text-red-400'
    : 'text-muted-foreground';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Radio className={`w-4 h-4 ${loading ? 'animate-pulse text-primary' : 'text-emerald-500'}`} />
            Live · <code className="font-mono">{symbol}</code>
            {quote?.feed && (
              <Badge variant="outline" className="text-[9px] uppercase">
                {quote.feed}
              </Badge>
            )}
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchQuote} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {error ? (
          <div className="text-xs text-red-500">{error}</div>
        ) : quote ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums">${fmtPrice(quote.last_price)}</span>
              <span className={`flex items-center gap-0.5 text-sm font-medium ${trendColor}`}>
                {trendIcon}
                {fmtPct(changePct)}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <div>Bid/Ask: <span className="font-mono text-foreground">${fmtPrice(quote.bid)} / ${fmtPrice(quote.ask)}</span></div>
              <div>Vol: <span className="font-mono text-foreground">{fmtVol(quote.daily_volume)}</span></div>
              <div>Open: <span className="font-mono text-foreground">${fmtPrice(quote.daily_open)}</span></div>
              <div>Prev close: <span className="font-mono text-foreground">${fmtPrice(quote.prev_close)}</span></div>
              <div>Day H: <span className="font-mono text-foreground">${fmtPrice(quote.daily_high)}</span></div>
              <div>Day L: <span className="font-mono text-foreground">${fmtPrice(quote.daily_low)}</span></div>
            </div>
            {quote.timestamp && (
              <p className="text-[9px] text-muted-foreground mt-2 font-mono">
                upd: {new Date(quote.timestamp).toLocaleString('it-IT')} · auto-refresh 30s
              </p>
            )}
          </>
        ) : loading ? (
          <div className="h-16 bg-muted/40 animate-pulse rounded" />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default LiveQuoteCard;
